/**
 * Support inbox — the operator's queue of customer conversations.
 *
 * Every row is a masked customer (never the full phone), the subject, its priority and
 * status, an unread badge for anything the operator side has not read, and when it last
 * moved. Filter by status and priority; click a row to open the thread. The list is served
 * by the same typed client as the rest of the console, so it is fully demoable offline.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import type { AdminThreadRow, SupportPriority, SupportStatus } from '../contracts/types.js';
import { SUPPORT_PRIORITIES, SUPPORT_STATUSES } from '../contracts/vocab.js';
import { Badge, Card, EmptyState, ErrorBox, LoadingState, Select } from '../components/ui.js';
import { ContactPhone } from '../components/ContactPhone.js';
import { SupportPriorityBadge, SupportStatusBadge } from '../components/badges.js';
import { useAsync } from '../hooks/useAsync.js';
import { humanize, relativeTime } from '../lib/format.js';

export function SupportInboxPage(): JSX.Element {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<SupportStatus | ''>('');
  const [priority, setPriority] = useState<SupportPriority | ''>('');

  const state = useAsync(
    () =>
      api.listInbox({
        ...(status !== '' ? { status } : {}),
        ...(priority !== '' ? { priority } : {}),
      }),
    [status, priority],
  );

  const items = state.data?.items ?? [];
  const openCount = items.filter((t) => t.status === 'open' || t.status === 'pending').length;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Support inbox</h1>
        <p className="page-head__sub">
          Customer chats and tickets. Open a conversation to read the full history, reply, assign it to
          yourself, and change its status or priority.
        </p>
      </div>

      <div className="toolbar">
        <Select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value as SupportStatus | '')}
        >
          <option value="">All statuses</option>
          {SUPPORT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {humanize(s)}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as SupportPriority | '')}
        >
          <option value="">All priorities</option>
          {SUPPORT_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {humanize(p)}
            </option>
          ))}
        </Select>
        <div className="toolbar__grow" />
      </div>

      <Card
        flush
        title="Conversations"
        actions={<Badge tone={openCount > 0 ? 'violet' : 'green'}>{openCount} open</Badge>}
      >
        {state.loading ? (
          <LoadingState label="Loading inbox…" />
        ) : state.error !== null ? (
          <ErrorBox message={state.error} />
        ) : items.length === 0 ? (
          <EmptyState glyph="✓" title="Inbox is clear" hint="No conversations match this filter." />
        ) : (
          <div className="table-wrap">
            <table className="table table--rows-clickable">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Subject</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Assigned</th>
                  <th className="table__num">Unread</th>
                  <th>Last message</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t: AdminThreadRow) => (
                  <tr key={t.id} onClick={() => navigate(`/support/threads/${t.id}`)}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{t.displayName ?? 'Customer'}</div>
                      {/*
                        The whole number, which `adminThreadRowSchema` publishes now.

                        An agent who has read a ticket and needs to ring the person back cannot
                        dial three missing digits, and the row used to be `maskedPhone`-only —
                        so the way back to the customer was to leave the inbox and find them in
                        Users. Where a row genuinely carries only a mask, the chip beside it
                        says so rather than letting the mask pass as the number.
                      */}
                      <div style={{ fontSize: 12 }}>
                        <ContactPhone row={t} copy={false} compact />
                      </div>
                    </td>
                    <td>
                      <span style={{ fontWeight: 550 }}>{t.subject}</span>{' '}
                      <span className="faint" style={{ fontSize: 12 }}>
                        · {humanize(t.type)}
                      </span>
                    </td>
                    <td>
                      <SupportPriorityBadge priority={t.priority} />
                    </td>
                    <td>
                      <SupportStatusBadge status={t.status} />
                    </td>
                    <td className="muted">{t.assignedAdminId === null ? 'Unassigned' : 'Assigned'}</td>
                    <td className="table__num">
                      {t.unreadForAdmin > 0 ? (
                        <span className="unread-pill">{t.unreadForAdmin}</span>
                      ) : (
                        <span className="faint">–</span>
                      )}
                    </td>
                    <td className="muted" title={t.lastMessageAt}>
                      {relativeTime(t.lastMessageAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
